import {
  loadGlobalSearchResults,
  GLOBAL_SEARCH_DEBOUNCE_MS,
  GLOBAL_SEARCH_CATALOG_MAX_AGE_MS,
  type PaddiqSearchResult,
} from '@kangaroo/shared';
import type { fetchDrivers } from '../drivers';
import type { fetchKarts } from '../karts';
import type { fetchDataFiles } from '../dataFiles';
import type { fetchTeamMembers } from '../teams';
import type { fetchTelemetryTracks } from '../telemetryContext';

export type SearchResult = PaddiqSearchResult;
export interface SearchSnapshot {
  query: string;
  loading: boolean;
  results: SearchResult[];
}
export interface SearchDependencies {
  fetchDrivers: typeof fetchDrivers;
  fetchKarts: typeof fetchKarts;
  fetchDataFiles: typeof fetchDataFiles;
  fetchTeamMembers: typeof fetchTeamMembers;
  fetchTelemetryTracks: typeof fetchTelemetryTracks;
  isCurrent: () => boolean;
}

export const SEARCH_DEBOUNCE_MS = GLOBAL_SEARCH_DEBOUNCE_MS;
export const SEARCH_CATALOG_MAX_AGE_MS = GLOBAL_SEARCH_CATALOG_MAX_AGE_MS;

/** One cache per mounted account/session/workspace. Nothing is persisted or shared between scopes. */
export function createGlobalSearchService(deps: SearchDependencies) {
  let snapshot: SearchSnapshot = { query: '', loading: false, results: [] };
  let generation = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const listeners = new Set<() => void>();
  type Catalog<T> = { request: Promise<T>; expiresAt: number };
  let members: Catalog<Awaited<ReturnType<typeof fetchTeamMembers>>> | undefined;
  let tracks: Catalog<Awaited<ReturnType<typeof fetchTelemetryTracks>>> | undefined;

  function publish(next: SearchSnapshot) {
    snapshot = next;
    listeners.forEach((listener) => listener());
  }

  function getMembers() {
    if (members && members.expiresAt > Date.now()) return members.request;
    const entry = {
      request: deps.fetchTeamMembers(),
      expiresAt: Date.now() + SEARCH_CATALOG_MAX_AGE_MS,
    };
    members = entry;
    void entry.request.catch(() => {
      if (members === entry) members = undefined;
    });
    return entry.request;
  }

  function getTracks() {
    if (tracks && tracks.expiresAt > Date.now()) return tracks.request;
    const entry = {
      request: deps.fetchTelemetryTracks(),
      expiresAt: Date.now() + SEARCH_CATALOG_MAX_AGE_MS,
    };
    tracks = entry;
    void entry.request.catch(() => {
      if (tracks === entry) tracks = undefined;
    });
    return entry.request;
  }

  function search(value: string) {
    const query = value.trim();
    const requestGeneration = ++generation;
    clearTimeout(timer);
    // Conceal previous query results immediately, including during the debounce.
    publish({ query, loading: query.length >= 2 && deps.isCurrent(), results: [] });
    if (query.length < 2 || !deps.isCurrent()) return;
    const isCurrent = () => generation === requestGeneration && deps.isCurrent();
    timer = setTimeout(() => {
      if (!isCurrent()) return;
      void loadGlobalSearchResults(
        query,
        {
          ...deps,
          fetchTeamMembers: getMembers,
          fetchTelemetryTracks: getTracks,
        },
        isCurrent,
      )
        .then((results) => {
          if (isCurrent()) publish({ query, loading: false, results });
        })
        .catch(() => {
          if (isCurrent()) publish({ query, loading: false, results: [] });
        });
    }, SEARCH_DEBOUNCE_MS);
  }

  function clear() {
    generation += 1;
    clearTimeout(timer);
    members = undefined;
    tracks = undefined;
    publish({ query: snapshot.query, loading: false, results: [] });
  }

  return {
    search,
    clear,
    invalidate: () => {
      clear();
      search(snapshot.query);
    },
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
